// File: Institutional_trend_mid_term.jsx

import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { List, ListItem, ListItemText, Divider, Box, Typography, Dialog, DialogTitle, DialogContent } from '@mui/material';
import api from '../functions/api';

import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';

const Institutional_Trends_Mid_Term_Table = () => {
    // console.log('Institutional Trends Mid Term - Component rendered');
    
    const marketPositioningData = useSelector(
        state => state.portfolio?.data?.Market_Positioning_Analysis || {}
    );
    const { columns = [], rows = [] } = marketPositioningData;
    const fullPortfolioData = useSelector(state => state.portfolio?.data);
    const portfolioStatus = useSelector(state => state.portfolio?.status);
      
    // console.log('Institutional Trends Mid Term - columns:', columns);
    // console.log('Institutional Trends Mid Term - rows:', rows);
    // console.log('Institutional Trends Mid Term - full state.portfolio.data:', fullPortfolioData);
    // console.log('Institutional Trends Mid Term - marketPositioningData:', marketPositioningData);
    // console.log('Institutional Trends Mid Term - portfolioStatus:', portfolioStatus);

    const [selectedVector, setSelectedVector] = useState(null);
    const [analysisData, setAnalysisData] = useState(null);
    const [open, setOpen] = useState(false);

    // Check if portfolio data is still loading
    
    if (portfolioStatus === 'loading') {
        return <div>Loading mid-term data...</div>;
    }
    
    if (!rows || rows.length === 0 || !columns || columns.length === 0) {
        return <div>No mid-term data available</div>;
    }

    // Get the latest row data
    const latestRow = rows[0];
    const fullResultJson = latestRow[columns.indexOf('full_result_json')];
    
    let top5Strong = [];
    let top5Weak = [];
    let overallTrend = 'Unknown';
    
    try {
        if (fullResultJson) {
            const jsonData = typeof fullResultJson === 'string' ? JSON.parse(fullResultJson) : fullResultJson;
            top5Strong = jsonData.top5_strong || [];
            top5Weak = jsonData.top5_weak || [];
            overallTrend = jsonData.overall_trend || 'Unknown';
            
            // Debug logging to understand data structure
            // console.log('Institutional Trends Mid Term - top5Strong:', top5Strong);
            // console.log('Institutional Trends Mid Term - top5Weak:', top5Weak);
            
            // Log first item structure to see available properties
            if (top5Strong.length > 0) {
                // console.log('Institutional Trends Mid Term - First strong item structure:', Object.keys(top5Strong[0]));
                // console.log('Institutional Trends Mid Term - First strong item:', top5Strong[0]);
            }
            if (top5Weak.length > 0) {
                // console.log('Institutional Trends Mid Term - First weak item structure:', Object.keys(top5Weak[0]));
                // console.log('Institutional Trends Mid Term - First weak item:', top5Weak[0]);
            }
        }
    } catch (error) {
        console.error('Error parsing full_result_json:', error);
    }

    const getSignalCategory = (value) => {
        if (typeof value !== 'string') return 'Neutral';
      
        if (value.includes('Strong Buy')) return 'Strong Buying';
        if (value.includes('Weak Buy')) return 'Weak Buying';
        if (value.includes('Weak Sell')) return 'Weak Selling';
        if (value.includes('Strong Sell')) return 'Strong Selling';
      
        return 'Neutral';
    };
    
    const getRowColor = (value) => {
        const category = getSignalCategory(value);
        switch (category) {
            case 'Strong Buying': return '#DFF2BF';
            case 'Weak Buying': return '#E6F5D0';
            case 'Weak Selling': return '#FEEFB3';
            case 'Strong Selling': return '#FF4C4C';
            default: return 'transparent';
        }
    };

    const handleRowClick = async (vectorName) => {
        setSelectedVector(vectorName);
        setOpen(true);
    
        try {
            const analysis_type = "Institutional_trend_mid_term";
            const response = await api.post("/Market_analysis", { analysis_type });
            // console.log('Analysis response:', response.data);
            setAnalysisData(response.data);
        } catch (error) {
            setAnalysisData({ error: "Failed to retrieve analysis" });
        }
    };

    return (
        <>
            <TableContainer component={Paper} sx={{ width: 'fit-content', overflow: 'auto', padding: '1px' }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell 
                                sx={{ fontSize: '14px', fontWeight: 'bold', padding: '0.8px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                onClick={() => handleRowClick('Market')}
                            >
                                Institutional Trends Mid Term
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {/* Top 3 Strong */}
                        <TableRow
                            onClick={() => handleRowClick('Top 3 Strong')}
                            sx={{
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: '#f5f5f5' },
                                backgroundColor: '#E6F5D0'
                            }}
                        >
                            <TableCell
                                sx={{
                                    fontSize: '10px',
                                    padding: '0.1px',
                                    whiteSpace: 'normal',
                                    fontWeight: 'bold',
                                    minWidth: '120px'
                                }}
                            >
                                Top 3 Strong
                            </TableCell>
                            <TableCell
                                sx={{
                                    fontSize: '10px',
                                    fontWeight: 'bold',
                                    padding: '0.1px',
                                    whiteSpace: 'normal',
                                    minWidth: '600px',
                                    width: '600px'
                                }}
                            >
                                {top5Strong.length > 0 ? (
                                    top5Strong.slice(0, 3).map((item, index) => (
                                        <div key={index} style={{ 
                                            marginBottom: '3px',
                                            fontSize: '11px',
                                            lineHeight: '1.2'
                                        }}>
                                            <div style={{ fontWeight: 'bold', marginBottom: '1px', whiteSpace: 'nowrap' }}>
                                                {item.region && item.sector ? `${item.region} ${item.sector}` : 
                                                 item.sector || item.region || item.net || 'Unknown'}
                                            </div>
                                            <div style={{ 
                                                color: item.pick === 'Strong Buying' ? '#00AA00' : 
                                                       item.pick === 'Weak Buying' ? '#00CC00' :
                                                       item.pick === 'Weak Selling' ? '#FF6600' :
                                                       item.pick === 'Strong Selling' ? '#FF0000' :
                                                       item.pick === 'Neutral' ? '#FF8800' : '#666666',
                                                fontSize: '10px',
                                                fontWeight: 'bold',
                                                marginTop: '0px',
                                                marginBottom: '3px'
                                            }}>
                                                {item.pick}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div style={{ 
                                        fontSize: '11px',
                                        color: '#666',
                                        fontStyle: 'italic'
                                    }}>
                                        No strong trends
                                    </div>
                                )}
                            </TableCell>
                        </TableRow>

                        {/* Top 3 Weak */}
                        <TableRow
                            onClick={() => handleRowClick('Top 3 Weak')}
                            sx={{
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: '#f5f5f5' },
                                backgroundColor: '#FEEFB3'
                            }}
                        >
                            <TableCell
                                sx={{
                                    fontSize: '10px',
                                    padding: '0.1px',
                                    whiteSpace: 'normal',
                                    fontWeight: 'bold',
                                    minWidth: '120px'
                                }}
                            >
                                Top 3 Weak
                            </TableCell>
                            <TableCell
                                sx={{
                                    fontSize: '10px',
                                    fontWeight: 'bold',
                                    padding: '0.1px',
                                    whiteSpace: 'normal',
                                    minWidth: '600px',
                                    width: '600px'
                                }}
                            >
                                {top5Weak.length > 0 ? (
                                    top5Weak.slice(0, 3).map((item, index) => (
                                        <div key={index} style={{ 
                                            marginBottom: '3px',
                                            fontSize: '11px',
                                            lineHeight: '1.2'
                                        }}>
                                            <div style={{ fontWeight: 'bold', marginBottom: '1px', whiteSpace: 'nowrap' }}>
                                                {item.region && item.sector ? `${item.region} ${item.sector}` : 
                                                 item.sector || item.region || item.net || 'Unknown'}
                                            </div>
                                            {/* Display actual trend data */}
                                            <div style={{ 
                                                color: item.pick === 'Strong Buying' ? '#00AA00' : item.pick === 'Weak Buying' ? '#00CC00' : item.pick === 'Weak Selling' ? '#FF6600' : item.pick === 'Strong Selling' ? '#FF0000' : item.pick === 'Neutral' ? '#FF8800' : '#666666',
                                                fontSize: '10px',
                                                textAlign: 'left',
                                                marginTop: '0px',
                                                fontWeight: 'bold'
                                            }}>
                                                {item.pick}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div style={{ 
                                        fontSize: '11px',
                                        color: '#666',
                                        fontStyle: 'italic'
                                    }}>
                                        No weak trends
                                    </div>
                                )}
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
      
            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                fullWidth
                maxWidth="lg"
                sx={{
                    "& .MuiDialog-paper": {
                        width: "90vw",
                        maxWidth: "1200px",
                        minWidth: "300px",
                        overflowX: "hidden"
                    }
                }}
            >
                <DialogTitle>{selectedVector} Analysis (Mid Term)</DialogTitle>
                <DialogContent
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        overflowX: "hidden",
                        wordBreak: "break-word",
                        whiteSpace: "pre-wrap",
                        width: "100%",
                        padding: "16px"
                    }}
                >
                    {analysisData ? (
                        <Box sx={{ width: "100%" }}>
                            {analysisData.result && (
                                <>
                                    <Typography variant="h6" gutterBottom><strong>Result:</strong></Typography>
                                    <Box sx={{
                                        display: "block",
                                        width: "100%",
                                        maxWidth: "100%",
                                        overflowWrap: "anywhere",
                                        wordBreak: "break-word",
                                        whiteSpace: "pre-wrap",
                                        lineBreak: "anywhere"
                                    }}>
                                        <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                                            {analysisData.result || "N/A"}
                                        </ReactMarkdown>
                                    </Box>
                                    <Divider sx={{ my: 2 }} />
                                </>
                            )}
      
                            <Typography variant="h6" gutterBottom><strong>Purpose:</strong></Typography>
                            <Box sx={{
                                display: "block",
                                width: "100%",
                                maxWidth: "100%",
                                overflowWrap: "anywhere",
                                wordBreak: "break-word",
                                whiteSpace: "pre-wrap",
                                lineBreak: "anywhere"
                            }}>
                                <ReactMarkdown>{analysisData.purpose}</ReactMarkdown>
                            </Box>
      
                            <Divider sx={{ my: 2 }} />
                        </Box>
                    ) : (
                        <Typography>Loading...</Typography>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default Institutional_Trends_Mid_Term_Table;
